import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, BookUser, Search } from 'lucide-react';
import { useCreateExternalTicket } from '@/hooks/useExternalTickets';
import type { CreateExternalTicketRequest } from '@/services/external-tickets.service';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import frequentContactsService from '@/services/frequentContacts.service';
import { useTranslation } from 'react-i18next';

interface CreateExternalTicketDialogProps {
  parentTicketId: string;
  parentTicketSubject: string;
  onSuccess?: (ticket: any) => void;
}

const CreateExternalTicketDialog: React.FC<CreateExternalTicketDialogProps> = ({
  parentTicketId,
  parentTicketSubject,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreateExternalTicketRequest>({
    title: `External Ticket SP-${Math.floor(Math.random() * 1000000)} [${parentTicketSubject}]`,
    description: `Automatisch generierte Beschreibung REF-${Math.floor(Math.random() * 100000)}`,
    external_party_name: `Kontakt-${Math.floor(Math.random() * 10000)}`,
    external_party_email: '',
    external_party_company: `Firma-${Math.floor(Math.random() * 10000)} GmbH`,
    priority: 'normal',
    type: 'other'
  });

  // Dialog açıldığında güncel parentTicketSubject ile title'ı güncelle
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        title: `External Ticket SP-${Math.floor(Math.random() * 1000000)} [${parentTicketSubject}]`
      }));
    }
  }, [open, parentTicketSubject]);

  const createExternalTicketMutation = useCreateExternalTicket();

  // Frequent contacts state
  const [isContactsPopoverOpen, setIsContactsPopoverOpen] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // Fetch frequent contacts sorted by ticket count (most used first)
  const { data: contactsData } = useQuery({
    queryKey: ['frequentContactsTop'],
    queryFn: () => frequentContactsService.getTopContacts(),
  });

  // Filter contacts based on search
  const filteredContacts = contactsData?.data.filter(contact =>
    contact.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(contactSearchQuery.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!parentTicketId) {
      toast.error(t('errors:fix.parentMissing', 'Haupt-Ticket-ID nicht gefunden'));
      return;
    }
    
    
    try {
      const result = await createExternalTicketMutation.mutateAsync({
        parentTicketId,
        data: formData
      });
      
      toast.success(t('errors:fix.extCreated', 'Interne Kommunikation erfolgreich erstellt'));
      setOpen(false);

      // onSuccess callback'i çağır ve yeni ticket'ı gönder
      if (onSuccess && result) {
        onSuccess(result);
      }

      // Reset form (random değerleriyle)
      setFormData({
        title: `External Ticket SP-${Math.floor(Math.random() * 1000000)} [${parentTicketSubject}]`,
        description: `Automatisch generierte Beschreibung REF-${Math.floor(Math.random() * 100000)}`,
        external_party_name: `Kontakt-${Math.floor(Math.random() * 10000)}`,
        external_party_email: '',
        external_party_company: `Firma-${Math.floor(Math.random() * 10000)} GmbH`,
        priority: 'normal',
        type: 'other'
      });
    } catch (error: any) {
      console.error('External ticket creation error:', error);
      toast.error(error?.response?.data?.message || t('errors:fix.extFailed', 'Interne Kommunikation konnte nicht erstellt werden'));
    }
  };

  const handleChange = (field: keyof CreateExternalTicketRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const typeOptions = [
    { value: 'supplier', label: t('agent:dept.supplier', 'Lieferant') },
    { value: 'warehouse', label: t('agent:dept.warehouse', 'Lager') },
    { value: 'shipping', label: t('agent:dept.shipping', 'Versand') },
    { value: 'vendor', label: t('agent:dept.vendor', 'Anbieter') },
    { value: 'partner', label: t('agent:dept.partner', 'Partner') },
    { value: 'other', label: t('agent:dept.other', 'Sonstiges') }
  ];

  const priorityOptions = [
    { value: 'low', label: t('domain:priority.low', 'Niedrig') },
    { value: 'normal', label: t('domain:priority.normal', 'Normal') },
    { value: 'high', label: t('domain:priority.high', 'Hoch') },
    { value: 'urgent', label: t('domain:priority.urgent', 'Dringend') }
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="internal-create-button flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition">
          <Plus className="h-3 w-3" />
          {t('common:x2.newInternalComm', 'Neue Interne Kommunikation')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-3xl border border-slate-200/70 bg-white/95 shadow-2xl backdrop-blur">
        <div className="rounded-2xl bg-[#0F99FD]/5 px-4 py-3 text-sm text-[#0c84e0]">
          {t('common:x2.newInternalCommDesc', 'Neue Interne Kommunikation für dieses Gespräch erstellen.')}
        </div>
        <DialogHeader>
          <DialogTitle>{t('common:x2.createInternalComm', 'Interne Kommunikation erstellen')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4">
          {/* Hidden field - Başlık */}
          <input
            type="hidden"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
          />

          {/* Hidden field - External Party Adı */}
          <input
            type="hidden"
            value={formData.external_party_name}
            onChange={(e) => handleChange('external_party_name', e.target.value)}
          />

          <div className="space-y-2">
            <Label htmlFor="external_party_email">{t('common:x2.emailReq', 'Email*')}</Label>
            <div className="relative">
              <Input
                id="external_party_email"
                type="email"
                value={formData.external_party_email}
                onChange={(e) => handleChange('external_party_email', e.target.value)}
                placeholder="email@example.com"
                className="rounded-full border border-slate-200 bg-white/80 pr-10 text-sm focus:border-[#0F99FD]/40 focus:ring-[#0F99FD]/30"
                required
              />
              <Popover open={isContactsPopoverOpen} onOpenChange={setIsContactsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 hover:bg-gray-100"
                  >
                    <BookUser className="h-4 w-4 text-gray-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 rounded-xl border border-slate-200/80 bg-white p-0 shadow-lg" align="end">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder={t('common:x1.searchContact', 'Kontakt suchen...')}
                        value={contactSearchQuery}
                        onChange={(e) => setContactSearchQuery(e.target.value)}
                        className="rounded-full border border-slate-200 bg-white/80 pl-8 text-sm focus:border-[#0F99FD]/40 focus:ring-[#0F99FD]/30"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredContacts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {contactSearchQuery ? 'Keine Kontakte gefunden' : 'Keine Kontakte vorhanden'}
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredContacts.map((contact) => (
                          <button
                            key={contact.id}
                            type="button"
                            onClick={() => {
                              handleChange('external_party_email', contact.email);
                              handleChange('type', contact.type || 'other');
                              setIsContactsPopoverOpen(false);
                              setContactSearchQuery('');
                            }}
                            className="flex w-full items-start gap-3 rounded-xl border border-slate-200/60 px-3 py-2 text-left text-sm text-slate-600 transition hover:border-[#0F99FD]/40 hover:bg-rose-50/60"
                          >
                            <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
                              <BookUser className="h-4 w-4 text-[#0F99FD]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {contact.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {contact.email}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Hidden field - Şirket */}
          <input
            type="hidden"
            value={formData.external_party_company}
            onChange={(e) => handleChange('external_party_company', e.target.value)}
          />

          <div className="space-y-2">
            <Label>{t('common:x2.department', 'Abteilung')}</Label>
            <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hidden field - Öncelik */}
          <input
            type="hidden"
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
          />

          {/* Hidden field - Açıklama */}
          <input
            type="hidden"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createExternalTicketMutation.isPending}
            >
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              type="submit"
              disabled={createExternalTicketMutation.isPending}
            >
              {createExternalTicketMutation.isPending ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExternalTicketDialog;
